import { createSlice } from "@reduxjs/toolkit";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_ID_KEY } from "../../Util/VMConstants";

const sessionFactory = new StorageFactory("session");
const userID = sessionFactory.get(SESSION_USER_ID_KEY);

const toServicePayload = (service) => ({
  service_id: service.service_id ?? service.id,
  service_name: service.service_name,
  service_charge: Number(service.service_charge),
});

const isSameService = (item, service) => {
  const catalogId = service.service_id ?? service.id;
  if (catalogId != null && item.service_id != null) {
    return String(item.service_id) === String(catalogId);
  }
  return (
    String(item.service_name || "").trim().toLowerCase() ===
    String(service.service_name || "").trim().toLowerCase()
  );
};

export const emptyEvent = {
  event_id: "",
  eventDetails: {
    event_date: "",
    event_type: "",
    event_name: "",
    sub_venue_id: "",
    sub_venue: "",
    booking_for: "",
    no_of_guests: "",
    check_in: "",
    check_out: "",
    rent_charges: "",
  },
  diningDetails: [
    {
      menu: "",
      no_of_plates: "",
      cost_per_plate: "",
      total_menu_cost: 0,
    },
  ],
  accomodationDetails: {
    charge_type: "",
    no_of_rooms: "",
    rooms_data: [],
    persons_data: [],
  },
  serviceDetails: [],
};

const initialState = {
  customer: {
    name: "",
    country_code: "",
    phone_no: "",
    email: "",
    appointment_date: [],
    address: "",
    notes: "",
    lead: "",
    enquiry_id: "",
    user_id: userID,
  },
  active_tab: 0,
  events: [structuredClone(emptyEvent)],
};

const addEnquirySlice = createSlice({
  name: "addEnquiry",
  initialState,
  reducers: {
    updateCustomerField(state, action) {
      const { field, value } = action.payload;
      state.customer[field] = value;
    },
    setEnquiryID(state, action) {
      state.customer.enquiry_id = action.payload;
    },
    updateEvent(state, action) {
      const { index, data } = action.payload;
      state.events.splice(index, 1, data);
    },
    removeEvent(state, action) {
      const { index } = action.payload;

      if (state.events.length === 1) return; // 🚫 Never delete last event

      if (index >= 0 && index < state.events.length) {
        state.events.splice(index, 1);
      }
    },
    // ✅ Update a specific menu inside diningDetails
    updateDiningMenu(state, action) {
      const { eventIndex, menuIndex, field, value } = action.payload;
      if (
        state.events[eventIndex] &&
        state.events[eventIndex].diningDetails[menuIndex]
      ) {
        state.events[eventIndex].diningDetails[menuIndex][field] = value;
      }
    },

    // ✅ Add a new menu entry
    addDiningMenu(state, action) {
      const { eventIndex } = action.payload;
      if (state.events[eventIndex]) {
        state.events[eventIndex].diningDetails.push({
          menu: "",
          no_of_plates: "",
          cost_per_plate: "",
          total_menu_cost: 0,
        });
      }
    },

    // ✅ Remove a menu entry
    removeDiningMenu(state, action) {
      const { eventIndex, menuIndex } = action.payload;
      if (
        state.events[eventIndex] &&
        state.events[eventIndex].diningDetails.length > menuIndex
      ) {
        state.events[eventIndex].diningDetails.splice(menuIndex, 1);
      }
    },

    updateAccomodationDetails(state, action) {
      const { index, field, value } = action.payload;
      if (state.events[index]) {
        state.events[index].accomodationDetails[field] = value;
      }
    },
    updateServiceDetails(state, action) {
      const { index, service } = action.payload;
      if (
        !state.events[index] ||
        !service?.service_name ||
        service?.service_charge === undefined
      ) {
        return;
      }

      const alreadySelected = state.events[index].serviceDetails.some((item) =>
        isSameService(item, service)
      );
      if (alreadySelected) return;

      state.events[index].serviceDetails.push(toServicePayload(service));
    },
    toggleServiceDetail(state, action) {
      const { index, service } = action.payload;
      if (!state.events[index] || !service?.service_name) return;

      const list = state.events[index].serviceDetails;
      const existingIndex = list.findIndex((item) => isSameService(item, service));
      if (existingIndex >= 0) {
        list.splice(existingIndex, 1);
      } else if (service.service_charge !== undefined) {
        list.push(toServicePayload(service));
      }
    },
    editServiceDetail(state, action) {
      const { eventIndex, serviceIndex, field, value } = action.payload;
      if (
        state.events[eventIndex] &&
        state.events[eventIndex].serviceDetails[serviceIndex]
      ) {
        state.events[eventIndex].serviceDetails[serviceIndex][field] = value;
      }
    },

    removeServiceDetail(state, action) {
      const { eventIndex, serviceIndex } = action.payload;
      if (
        state.events[eventIndex] &&
        state.events[eventIndex].serviceDetails.length > serviceIndex
      ) {
        state.events[eventIndex].serviceDetails.splice(serviceIndex, 1);
      }
    },
    setCatalogServiceDetailsOnAllEvents(state, action) {
      const services = (action.payload || []).map(toServicePayload);
      state.events.forEach((event) => {
        event.serviceDetails = services.map((item) => ({ ...item }));
      });
    },
    setCatalogServiceDetailsOnEvent(state, action) {
      const { index, services } = action.payload || {};
      if (index == null || !state.events[index]) return;
      state.events[index].serviceDetails = (services || []).map((item) => ({
        ...toServicePayload(item),
      }));
    },
    syncCatalogServiceOnEvents(state, action) {
      const catalog = action.payload;
      if (!catalog?.id) return;
      state.events.forEach((event) => {
        event.serviceDetails.forEach((item) => {
          if (String(item.service_id) === String(catalog.id)) {
            item.service_name = catalog.service_name;
            item.service_charge = Number(catalog.service_charge);
          }
        });
      });
    },
    removeCatalogServiceFromEvents(state, action) {
      const serviceId = action.payload;
      if (serviceId == null) return;
      state.events.forEach((event) => {
        event.serviceDetails = event.serviceDetails.filter(
          (item) => String(item.service_id) !== String(serviceId)
        );
      });
    },

    addEvent(state) {
      state.events.push(structuredClone(emptyEvent));
    },

    setEvents(state, action) {
      state.events = action.payload;
    },

    resetForm() {
      return structuredClone(initialState);
    },

    updateChargeType(state, action) {
      const { index, chargeType } = action.payload;
      if (state.events[index]) {
        state.events[index].accomodationDetails.charge_type = chargeType;
        if (chargeType === "room") {
          state.events[index].accomodationDetails.rooms_data = [
            { room_name: "", room_type: "", room_cost: "" },
          ];
          state.events[index].accomodationDetails.persons_data = [];
        } else if (chargeType === "person") {
          state.events[index].accomodationDetails.persons_data = [
            { persons_name: "", person_phone: "" },
          ];
          state.events[index].accomodationDetails.rooms_data = [];
        }
      }
    },
  },
});

export const {
  updateCustomerField,
  setEnquiryID,
  updateEvent,
  removeEvent,
  updateDiningMenu,
  addDiningMenu,
  removeDiningMenu,
  updateAccomodationDetails,
  updateServiceDetails,
  toggleServiceDetail,
  editServiceDetail,
  removeServiceDetail,
  setCatalogServiceDetailsOnAllEvents,
  setCatalogServiceDetailsOnEvent,
  syncCatalogServiceOnEvents,
  removeCatalogServiceFromEvents,
  addEvent,
  setEvents,
  resetForm,
  updateChargeType,
} = addEnquirySlice.actions;

export default addEnquirySlice.reducer;
